import { z } from 'zod';
import { PaginatedResponseSchema, PaginationQuerySchema } from './pagination';

/**
 * Контракты Блока 4 «Подбери ручки и крючки» (PRD `docs/prd/v4/06-block-4-handles.md`).
 *
 * Соответствие источникам:
 * - Таблица БД (`docs/architecture/data-model.md` §4): один цвет = один SKU = один Global_ID.
 *   Поля `color` и `shape` в БД — single-value, **не** JSON-массив. Multi-select в UI
 *   реализуется через `WHERE color IN (...) AND shape IN (...)` — не через JSON-массив в строке.
 * - API (`docs/architecture/api-contracts.md` §3): запрос принимает массивы `colors[]`/`shapes[]`,
 *   ответ — single value на каждый item (потому что один SKU = один цвет).
 * - PRD §06: цвет и форма комбинируются через AND, внутри каждого фильтра — OR.
 *
 * Расхождения, которые мы НЕ применяем (зафиксировано в Phase 1 commit message):
 * - PRD §06 «JSON-массив цветов в БД» — устаревшая формулировка, override через data-model.md.
 * - api-contracts.md `color: z.string()` (singular) — в этом файле уже `colors: z.array(...)`.
 */

/** Дискриминатор внутри таблицы `handles`. URL может содержать plural — это псевдокод PRD. */
export const HandleKindSchema = z.enum(['handle', 'hook']);
export type HandleKind = z.infer<typeof HandleKindSchema>;

/** GET /api/handles — query string. Multi-select по PRD §06. */
export const HandlesListQuerySchema = z
  .object({
    kind: HandleKindSchema.optional(),
    // ADR-0076: мульти-выбор брендов (как colors/shapes). «Без бренда» = '' в массиве.
    brands: z.array(z.string()).optional(),
    colors: z.array(z.string()).optional(),
    shapes: z.array(z.string()).optional(),
    mountType: z.string().optional(),
  })
  .merge(PaginationQuerySchema);

/**
 * Output-тип после parse: `limit` всегда заполнен (default=50).
 * Используется во внутренних обработчиках после Zod-валидации.
 */
export type HandlesListQuery = z.infer<typeof HandlesListQuerySchema>;

/**
 * Input-тип до parse: `limit` опционален. Подходит для функций, которые
 * принимают «сырой» фильтр от пользователя/тестов без обязательной валидации.
 */
export type HandlesListQueryInput = z.input<typeof HandlesListQuerySchema>;

/** Item в списке (для каталога 2.4.2 и грида карточек). Минимальный набор для UI. */
export const HandleListItemSchema = z.object({
  globalId: z.string(),
  name: z.string(),
  brand: z.string(),
  kind: HandleKindSchema,
  color: z.string(),
  shape: z.string().nullable(),
  primaryPhotoKey: z.string().nullable(),
});

export type HandleListItem = z.infer<typeof HandleListItemSchema>;

export const HandlesListResponseSchema = PaginatedResponseSchema(HandleListItemSchema);
export type HandlesListResponse = z.infer<typeof HandlesListResponseSchema>;

/** Полная карточка ручки/крючка (Экран 2.4.3). */
export const HandleSchema = z.object({
  globalId: z.string(),
  name: z.string(),
  brand: z.string(),
  kind: HandleKindSchema,
  color: z.string(),
  shape: z.string().nullable(),
  mountType: z.string().nullable(),
  sizeMm: z.string().nullable(),
  photoKeys: z.array(z.string()),
  // ADR-0095 A2: короткий алиас карточки `<prefix><share_hash>`, навешивается на
  // API-границе (edge/MSW), optional/аддитивно. См. `packages/shared/src/share/short-id.ts`.
  shortId: z.string().optional(),
});

export type Handle = z.infer<typeof HandleSchema>;

/**
 * Per-channel СЧЁТЧИКИ кнопок дилера (ADR-0083 — анти-парсинг P-031). Сколько
 * валидных кнопок отдать в каждом канале. ВЫЧИСЛЯЕМЫ из номеров дилера на бэке
 * (`channelCounts`), в D1 не хранятся. WA/TG/MAX сегодня численно равны (одно
 * множество мобильных), держим раздельными осознанно (ADR-0083 §3.1).
 */
export const ChannelCountsSchema = z.object({
  whatsapp: z.number().int().nonnegative(),
  telegram: z.number().int().nonnegative(),
  max: z.number().int().nonnegative(),
  call: z.number().int().nonnegative(),
});

export type ChannelCounts = z.infer<typeof ChannelCountsSchema>;

/**
 * Контакты дилера — МАССИВЫ номеров (ADR-0060). СЕРВЕРНАЯ форма (D1 / фикстуры):
 * «найти в магазинах» резолвит запрос по ВСЕМ каналам (несколько менеджеров).
 * Эти массивы НЕ уходят на клиент (Фаза 3 анти-парсинга) — наружу через
 * `/shops` идёт только {@link ChannelCountsSchema}; номера резолвятся поиндексно
 * при клике (`/api/dealers/:id/contact`). `telegram`/`maxId` (username-каналы)
 * на экране контакта не рендерятся (ADR-0064 Фаза 6.2).
 */
export const DealerContactsSchema = z.object({
  phones: z.array(z.string()),
  whatsapp: z.array(z.string()),
  telegram: z.array(z.string()),
  maxId: z.array(z.string()),
});

export type DealerContacts = z.infer<typeof DealerContactsSchema>;

/**
 * Полная запись дилера — СЕРВЕРНЫЙ тип (D1 `dealers`, фикстуры, резолв-эндпоинт).
 * Содержит МАССИВЫ номеров (`contacts`). НЕ сериализуется в `/shops`-ответ —
 * для wire есть проекция {@link ShopContactSchema} (`dealerToShopContact`,
 * `packages/shared/src/domain/dealer-channels.ts`), которая отбрасывает массивы и
 * оставляет только счётчики (ADR-0083 Фаза 3 — анти-парсинг P-031). `id` = dealerId.
 *
 * Раздвой типа (а НЕ удаление полей из `ShopContact`): резолвер `/contact` и
 * D1-маппинг `rowToDealer` читают `contacts.whatsapp/phones` — им нужны массивы;
 * клиент номера больше не видит. P-001: номера/бренды/id — verbatim.
 */
export const DealerSchema = z.object({
  id: z.string(),
  name: z.string(),
  city: z.string(),
  address: z.string().nullable(),
  /** Канон-бренды каталога (verbatim, P-001), для матчинга декор↔магазин. Пустой = фурнитурный. */
  brands: z.array(z.string()),
  /**
   * Бренды железа (фурнитура/ручки/крючки) дилера — СЕРВЕРНОЕ поле, CI-матч
   * (ADR-0089 §3.4/§5.1). Изолировано от декорного `brands` (verbatim-матч, P-033).
   * Поле наполняется Фазой B (импорт+D1); в Фазе A — пусто.
   *
   * `.optional()` (НЕ `.default([])`, как в черновике ADR §5.1): `.default()` в Zod
   * делает OUTPUT-тип `Dealer` обязательным по этому полю → сломал бы 333-записную
   * СГЕНЕРИРОВАННУЮ `SHOPS_FIXTURE` (и `FURNITURE_DEALERS_FIXTURE`), где поля ещё
   * нет (их нельзя править руками — регенерируются Фазой B). `.optional()` сохраняет
   * совместимость без правок фикстур (output `string[] | undefined`); потребители
   * читают `fittingBrands ?? []` (матчер §4.1). Семантика «нет поля = пусто» — та же.
   *
   * В wire `ShopContact` НЕ уходит (`dealerToShopContact` явно отбрасывает,
   * ADR-0083 Ф3 / §5.2). P-001: бренды verbatim; CI — только в матчере.
   */
  fittingBrands: z.array(z.string()).optional(),
  website: z.string().nullable().optional(),
  /** t.me/<channel> — публичный канал магазина (ADR-0060). */
  telegramChannel: z.string().nullable().optional(),
  instagram: z.string().nullable().optional(),
  contacts: DealerContactsSchema,
});

export type Dealer = z.infer<typeof DealerSchema>;

/**
 * Карточка магазина для секции «Найти в магазинах» (Экран 2.4.4) — WIRE-тип
 * `/shops`-ответа. Идентичная схема для декоров, красок, ручек, фурнитуры
 * (`/api/decors/{id}/shops` и пр.), см. `docs/architecture/api-contracts.md` §3.
 *
 * ADR-0083 Фаза 3 (анти-парсинг P-031): `contacts` несёт ТОЛЬКО per-channel
 * счётчики ({@link ChannelCountsSchema}), номеров-массивов в wire НЕТ. `counts`
 * ОБЯЗАТЕЛЕН — единственный продюсер wire-`ShopContact` это `dealerToShopContact`,
 * который счётчики всегда считает (раньше был `optional` ради аддитивной Фазы 1).
 * Top-level поля (id=dealerId, name, city, address, brands, …) — как были.
 */
export const ShopContactSchema = z.object({
  id: z.string(),
  name: z.string(),
  city: z.string(),
  address: z.string().nullable(),
  /** Канон-бренды каталога (verbatim, P-001), для матчинга декор↔магазин. Пустой = фурнитурный. */
  brands: z.array(z.string()),
  website: z.string().nullable().optional(),
  /** t.me/<channel> — публичный канал магазина (ADR-0060). */
  telegramChannel: z.string().nullable().optional(),
  instagram: z.string().nullable().optional(),
  contacts: z.object({
    counts: ChannelCountsSchema,
  }),
});

export type ShopContact = z.infer<typeof ShopContactSchema>;

/**
 * Канал резолв-эндпоинта `GET /api/dealers/:dealerId/contact` (ADR-0083 §3.2).
 * `whatsapp|telegram|call` → JSON `{ url }`; `max` → JSON `{ number }`.
 *
 * ADR-0083 Amendment (302 → JSON-резолв): авторизация приложения — header-bearer
 * (`apiClient.authToken`); браузер-навигация (`openExternal`) заголовок НЕ несёт,
 * поэтому 302-эндпоинт в приложении дал бы 401. Эндпоинт отдаёт JSON, клиент
 * резолвит авторизованным fetch'ем и сам зовёт `openExternal`. Анти-парсинг тот
 * же: URL/номер резолвятся ПРИ КЛИКЕ под авторизацией, в DOM списка их нет.
 */
export const DealerChannelSchema = z.enum(['whatsapp', 'telegram', 'call', 'max']);
export type DealerChannel = z.infer<typeof DealerChannelSchema>;

/**
 * Ответ резолв-эндпоинта для каналов `whatsapp|telegram|call` (ADR-0083 §3.2,
 * Amendment): целевой deeplink (`wa.me`/`t.me/+`/`tel:`), построенный на бэке
 * (`buildDealerRedirectUrl`). Клиент сам зовёт `openExternal(url)`.
 */
export const DealerContactUrlResponseSchema = z.object({
  url: z.string(),
});
export type DealerContactUrlResponse = z.infer<typeof DealerContactUrlResponseSchema>;

/**
 * Ответ резолв-эндпоинта для канала `max` (ADR-0083 §3.3): номер резолвится при
 * клике для оверлея ручного поиска в MAX (deeplink-чата по номеру в MAX нет).
 */
export const DealerContactNumberResponseSchema = z.object({
  number: z.string(),
});
export type DealerContactNumberResponse = z.infer<typeof DealerContactNumberResponseSchema>;

/**
 * Унифицированный ответ резолв-эндпоинта `GET /api/dealers/:dealerId/contact`
 * (ADR-0083 Amendment). Форма зависит от канала: `whatsapp|telegram|call` → `{ url }`,
 * `max` → `{ number }`. Клиент знает канал запроса и сужает: `'url' in res` /
 * `'number' in res`.
 */
export const DealerContactResponseSchema = z.union([
  DealerContactUrlResponseSchema,
  DealerContactNumberResponseSchema,
]);
export type DealerContactResponse = z.infer<typeof DealerContactResponseSchema>;

export const ShopsResponseSchema = z.object({
  shops: z.array(ShopContactSchema),
});

export type ShopsResponse = z.infer<typeof ShopsResponseSchema>;

/**
 * Ответ `GET /api/dealers/cities` (ADR-0060 §3, Фаза 5): список городов для
 * пикера — distinct из таблицы `dealers`, в канон-порядке (`sortCanonCities`).
 * Не sensitive (список городов — не ПДн), JWT не требует.
 */
export const CitiesResponseSchema = z.object({
  cities: z.array(z.string()),
});

export type CitiesResponse = z.infer<typeof CitiesResponseSchema>;
